/**
 * Unified error handling utilities for both web and mobile platforms
 * Provides a consistent way to display errors regardless of platform
 */

import { Platform } from 'react-native';
import { showAlert } from './alert';

/**
 * Error display function type
 * This allows us to inject platform-specific error handlers
 */
export type ErrorDisplayFunction = (message: string, title?: string) => void;

/**
 * Default error handler for mobile (uses alerts)
 */
const defaultMobileErrorHandler: ErrorDisplayFunction = (message: string, title?: string) => {
  showAlert(title || 'Error', message);
};

/**
 * Global error handler that can be overridden per context
 * Web contexts (like toast context) can override this
 */
let globalErrorHandler: ErrorDisplayFunction = defaultMobileErrorHandler;

/**
 * Set a custom global error handler
 * Used by web contexts to inject toast notifications
 */
export const setGlobalErrorHandler = (handler: ErrorDisplayFunction) => {
  globalErrorHandler = handler;
};

/**
 * Reset error handler to default (mobile alerts)
 */
export const resetGlobalErrorHandler = () => {
  globalErrorHandler = defaultMobileErrorHandler;
};

/**
 * Show error using the appropriate method for the current platform
 * @param message - Error message to display
 * @param title - Optional error title (defaults to 'Error')
 */
export const showError = (message: string, title?: string) => {
  globalErrorHandler(message, title);
};

/**
 * Extract a user-friendly message from an API error.
 *
 * Post-RES-97 the backend emits one envelope:
 *   { success: false, error: { code, status, message, description?, details? } }
 * carried by RTK Query as { status, data } or axios as { response: { data } }.
 * Legacy TSOA / bare-string branches removed 2026-07 (RES-119).
 */
export const extractErrorMessage = (error: any, defaultMessage: string = 'An error occurred'): string => {
  if (typeof error === 'string') return error;

  const data = error?.data ?? error?.response?.data ?? error;
  const envelope = data?.error;
  if (envelope && typeof envelope === 'object') {
    if (typeof envelope.description === 'string') return envelope.description;
    if (typeof envelope.message === 'string') return envelope.message;
  }
  if (typeof data?.description === 'string') return data.description;
  if (typeof data?.message === 'string') return data.message;
  if (typeof error?.message === 'string') return error.message;

  return defaultMessage;
};

/**
 * Handle Google login errors with appropriate messaging
 */
export const handleGoogleLoginError = (error: any, errorHandler?: ErrorDisplayFunction) => {
  const message = extractErrorMessage(error, 'Google login failed');
  const title = 'Sign-in Error';
  
  if (errorHandler) {
    errorHandler(message, title);
  } else {
    showError(message, title);
  }
};

/**
 * Handle Apple login errors with appropriate messaging
 */
export const handleAppleLoginError = (error: any, errorHandler?: ErrorDisplayFunction) => {
  const message = extractErrorMessage(error, 'Apple login failed');
  const title = 'Sign-in Error';
  
  if (errorHandler) {
    errorHandler(message, title);
  } else {
    showError(message, title);
  }
};

/**
 * Handle standard login errors
 */
export const handleLoginError = (error: any, errorHandler?: ErrorDisplayFunction) => {
  const message = extractErrorMessage(error, 'Login failed');
  const title = 'Login Error';
  
  if (errorHandler) {
    errorHandler(message, title);
  } else {
    showError(message, title);
  }
};

